import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container, Box, Paper, Typography, Grid, Button, TextField, Switch, FormControlLabel, Divider, Alert, CircularProgress, Chip, Slider } from "@mui/material";
import { ShieldCheck, ShieldOff, Lock, KeyRound, Clock, AlertTriangle, CheckCircle, Info, Smartphone } from "lucide-react";
import TitleBox from "../Shared/TitleBox";
import { fetchSecuritySettings, updateSecuritySettings } from "../../api/index";
const DEFAULTS = {
  passwordPolicy: {
    minLength: 8,
    requireUppercase: false,
    requireLowercase: false,
    requireNumbers: false,
    requireSpecialChars: false,
    expiryDays: 0
  },
  accountLockout: {
    enabled: true,
    maxAttempts: 5,
    lockoutDurationMinutes: 30
  },
  sessionSettings: {
    forceLogoutOnPasswordChange: true
  }
};
function SectionHeader({
  icon,
  title,
  description
}) {
  return <Box sx={{
    display: "flex",
    alignItems: "flex-start",
    gap: 1.5,
    mb: 3
  }}>
      <Box sx={{
      color: "primary.main",
      mt: 0.25
    }}>{icon}</Box>
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
        {description && <Typography variant="body2" color="text.secondary">{description}</Typography>}
      </Box>
    </Box>;
}
function PolicyChip({
  active,
  label
}) {
  return <Chip size="small" icon={active ? <CheckCircle size={12} /> : undefined} label={label} color={active ? "success" : "default"} variant={active ? "filled" : "outlined"} sx={{
    fontSize: "0.75rem"
  }} />;
}

const SECURITY_POLICY_LABEL_KEYS = {
  requireUppercase: "Uppercase (A–Z)",
  requireLowercase: "Lowercase (a–z)",
  requireNumbers: "Numbers (0–9)",
  requireSpecialChars: "Special chars (!@#…)"
};

const RATE_LIMIT_LABEL_KEYS = {
  loginAttempts: "Login attempts",
  generalApi: "General API",
  sensitiveOps: "Sensitive ops"
};

const getSecurityPolicyLabelKey = key => SECURITY_POLICY_LABEL_KEYS[key] || key;
const getRateLimitLabelKey = key => RATE_LIMIT_LABEL_KEYS[key] || key;

export default function SecuritySettings() {
  const {
    t,
    i18n
  } = useTranslation("admin");
  const currentLang = i18n.resolvedLanguage;
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  useEffect(() => {
    fetchSecuritySettings().then(r => {
      const d = r.data;
      if (d && Object.keys(d).length > 0) setForm({
        ...DEFAULTS,
        ...d
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const setPolicy = (key, val) => setForm(f => ({
    ...f,
    passwordPolicy: {
      ...f.passwordPolicy,
      [key]: val
    }
  }));
  const setLockout = (key, val) => setForm(f => ({
    ...f,
    accountLockout: {
      ...f.accountLockout,
      [key]: val
    }
  }));
  const setSession = (key, val) => setForm(f => ({
    ...f,
    sessionSettings: {
      ...f.sessionSettings,
      [key]: val
    }
  }));
  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await updateSecuritySettings(form);
      setSuccess(t("Security settings saved successfully."));
    } catch (e) {
      setError(e.response?.data?.message || t("Failed to save settings."));
    } finally {
      setSaving(false);
    }
  };

  // Derive active policy count for the overview banner
  const pp = form.passwordPolicy || {};
  const al = form.accountLockout || {};
  const ss = form.sessionSettings || {};
  const activeRules = [pp.minLength > 8, pp.requireUppercase, pp.requireLowercase, pp.requireNumbers, pp.requireSpecialChars, pp.expiryDays > 0, al.enabled, ss.forceLogoutOnPasswordChange].filter(Boolean).length;
  const strengthLevel = activeRules <= 2 ? {
    label: t("Basic"),
    color: "#EF6C00"
  } : activeRules <= 5 ? {
    label: t("Moderate"),
    color: "#1565C0"
  } : {
    label: t("Strong"),
    color: "#2E7D32"
  };
  if (loading) {
    return <Container maxWidth={false} className="AppContainer-root">
        <Box sx={{
        display: "flex",
        justifyContent: "center",
        py: 10
      }}>
          <CircularProgress />
        </Box>
      </Container>;
  }
  return <Container maxWidth={false} className="AppContainer-root">
      <TitleBox title={t("Security Settings")} subTitle={t("Manage password policies, account lockout, and session security")} allowPrint={false} />

      {error && <Alert severity="error" sx={{
      mb: 3
    }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{
      mb: 3
    }} onClose={() => setSuccess("")}>{success}</Alert>}

      <Grid container spacing={3}>

        {/* ── Left column ─────────────────────────────────────────── */}
        <Grid item xs={12} lg={8}>

          {/* Password Policy */}
          <Paper elevation={1} sx={{
          borderRadius: 2,
          mb: 3,
          overflow: "hidden"
        }}>
            <Box sx={{
            p: 3
          }}>
              <SectionHeader icon={<Lock size={20} />} title={t("Password Policy")} description={t("Rules applied when users create or change their password.")} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" gutterBottom fontWeight={600}>{t("Minimum Password Length:")}<strong>{pp.minLength}</strong>
                  </Typography>
                  <Slider value={pp.minLength || 8} onChange={(_, v) => setPolicy("minLength", v)} min={6} max={32} step={1} marks={[{
                  value: 6,
                  label: "6"
                }, {
                  value: 12,
                  label: "12"
                }, {
                  value: 20,
                  label: "20"
                }, {
                  value: 32,
                  label: "32"
                }]} valueLabelDisplay="auto" sx={{
                  color: "primary.main"
                }} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" gutterBottom fontWeight={600}>{t("Password Expiry (days)")}</Typography>
                  <TextField type="number" size="small" fullWidth value={pp.expiryDays ?? 0} onChange={e => setPolicy("expiryDays", Math.max(0, parseInt(e.target.value) || 0))} inputProps={{
                  min: 0,
                  max: 365
                }} helperText={t("Set to 0 to disable expiry")} />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" fontWeight={600} sx={{
                  mb: 1
                }}>{t("Required Character Types")}</Typography>
                  <Box sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1
                }}>
                    {[{
                    key: "requireUppercase"
                  }, {
                    key: "requireLowercase"
                  }, {
                    key: "requireNumbers"
                  }, {
                    key: "requireSpecialChars"
                  }].map(({
                    key
                  }) => <FormControlLabel key={key} control={<Switch checked={!!pp[key]} onChange={e => setPolicy(key, e.target.checked)} size="small" />} label={<Typography variant="body2">{t(getSecurityPolicyLabelKey(key))}</Typography>} sx={{
                    mr: 2
                  }} />)}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* Account Lockout */}
          <Paper elevation={1} sx={{
          borderRadius: 2,
          mb: 3,
          overflow: "hidden"
        }}>
            <Box sx={{
            px: 3,
            py: 2,
            bgcolor: al.enabled ? "warning.50" : "grey.50",
            borderBottom: "1px solid",
            borderColor: al.enabled ? "warning.200" : "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}>
              <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5
            }}>
                {al.enabled ? <ShieldCheck size={20} color="#E65100" /> : <ShieldOff size={20} color="#9e9e9e" />}
                <Box>
                  <Typography fontWeight={700}>{t("Account Lockout")}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {al.enabled ? t("Accounts lock after {{attempts}} failed attempts for {{minutes}} min", {
                    attempts: al.maxAttempts,
                    minutes: al.lockoutDurationMinutes
                  }) : t("Accounts are never locked on failed login attempts")}
                  </Typography>
                </Box>
              </Box>
              <Switch checked={!!al.enabled} onChange={e => setLockout("enabled", e.target.checked)} />
            </Box>

            {al.enabled && <Box sx={{
            p: 3
          }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>{t("Max Failed Attempts:")}<strong>{al.maxAttempts}</strong>
                    </Typography>
                    <Slider value={al.maxAttempts || 5} onChange={(_, v) => setLockout("maxAttempts", v)} min={1} max={20} step={1} marks={[{
                  value: 1,
                  label: "1"
                }, {
                  value: 5,
                  label: "5"
                }, {
                  value: 10,
                  label: "10"
                }, {
                  value: 20,
                  label: "20"
                }]} valueLabelDisplay="auto" sx={{
                  color: "warning.main"
                }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>{t("Lockout Duration:")}<strong>{al.lockoutDurationMinutes}{t("min")}</strong>
                    </Typography>
                    <Slider value={al.lockoutDurationMinutes || 30} onChange={(_, v) => setLockout("lockoutDurationMinutes", v)} min={5} max={1440} step={5} marks={[{
                  value: 5,
                  label: "5m"
                }, {
                  value: 30,
                  label: "30m"
                }, {
                  value: 60,
                  label: "1h"
                }, {
                  value: 1440,
                  label: "24h"
                }]} valueLabelDisplay="auto" valueLabelFormat={v => v < 60 ? `${v}m` : `${Math.floor(v / 60)}h${v % 60 ? ` ${v % 60}m` : ""}`} sx={{
                  color: "warning.main"
                }} />
                  </Grid>
                </Grid>
              </Box>}
          </Paper>

          {/* Session Security */}
          <Paper elevation={1} sx={{
          borderRadius: 2,
          mb: 3
        }}>
            <Box sx={{
            p: 3
          }}>
              <SectionHeader icon={<Clock size={20} />} title={t("Session Security")} description={t("Controls how active sessions behave after security events.")} />
              <FormControlLabel control={<Switch checked={!!ss.forceLogoutOnPasswordChange} onChange={e => setSession("forceLogoutOnPasswordChange", e.target.checked)} />} label={<Box>
                    <Typography variant="body2" fontWeight={600}>{t("Force logout on password change")}</Typography>
                    <Typography variant="caption" color="text.secondary">{t("Invalidates all existing sessions when a user changes their password.")}</Typography>
                  </Box>} />
            </Box>
          </Paper>

          {/* Save */}
          <Box sx={{
          display: "flex",
          justifyContent: "flex-end"
        }}>
            <Button variant="contained" size="large" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : <ShieldCheck size={16} />} sx={{
            px: 4
          }}>
              {saving ? t("Saving…") : t("Save Security Settings")}
            </Button>
          </Box>
        </Grid>

        {/* ── Right column ─────────────────────────────────────────── */}
        <Grid item xs={12} lg={4}>

          {/* Security Strength */}
          <Paper elevation={1} sx={{
          borderRadius: 2,
          mb: 3,
          overflow: "hidden"
        }}>
            <Box sx={{
            p: 3
          }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{
              mb: 2
            }}>{t("Security Posture")}</Typography>
              <Box sx={{
              p: 2,
              borderRadius: 2,
              textAlign: "center",
              mb: 2,
              bgcolor: `${strengthLevel.color}14`,
              border: "1px solid",
              borderColor: `${strengthLevel.color}40`
            }}>
                <Typography variant="h4" fontWeight={800} sx={{
                color: strengthLevel.color
              }}>
                  {strengthLevel.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {activeRules}{t("of 8 security rules active")}</Typography>
              </Box>

              <Box sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75
            }}>
                <PolicyChip active={pp.minLength > 8} label={t("Min {{count}} chars", {
              count: pp.minLength
            })} />
                <PolicyChip active={!!pp.requireUppercase} label={t("Uppercase")} />
                <PolicyChip active={!!pp.requireLowercase} label={t("Lowercase")} />
                <PolicyChip active={!!pp.requireNumbers} label={t("Numbers")} />
                <PolicyChip active={!!pp.requireSpecialChars} label={t("Special chars")} />
                <PolicyChip active={pp.expiryDays > 0} label={pp.expiryDays > 0 ? t("Expires {{days}}d", {
              days: pp.expiryDays
            }) : t("No expiry")} />
                <PolicyChip active={!!al.enabled} label={t("Lockout")} />
                <PolicyChip active={!!ss.forceLogoutOnPasswordChange} label={t("Session invalidation")} />
              </Box>
            </Box>
          </Paper>

          {/* Rate Limiting Info */}
          <Paper elevation={1} sx={{
          borderRadius: 2,
          mb: 3
        }}>
            <Box sx={{
            p: 3
          }}>
              <SectionHeader icon={<AlertTriangle size={20} />} title={t("Rate Limiting")} description={t("Server-level limits (read-only).")} />
              {[{
              key: "loginAttempts",
              value: "50 / hour per IP"
            }, {
              key: "generalApi",
              value: "1,000 / 15 min per IP"
            }, {
              key: "sensitiveOps",
              value: "50 / hour per IP"
            }].map(({
              key,
              value
            }) => <Box key={key} sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              "&:last-child": {
                borderBottom: "none"
              }
            }}>
                  <Typography variant="body2" color="text.secondary">{t(getRateLimitLabelKey(key))}</Typography>
                  <Chip label={value} size="small" variant="outlined" />
                </Box>)}
            </Box>
          </Paper>

          {/* Two-Factor Auth shortcut */}
          <Paper elevation={1} sx={{
          borderRadius: 2,
          p: 3,
          bgcolor: "success.50",
          border: "1px solid",
          borderColor: "success.200"
        }}>
            <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 1.5
          }}>
              <Smartphone size={20} color="#2e7d32" />
              <Typography fontWeight={700}>{t("Two-Factor Authentication")}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{
            mb: 2
          }}>{t("Protect your login with a one-time code from an authenticator app.")}</Typography>
            <Button component={Link} to={`/${currentLang}/app/settings/mfa`} variant="contained" color="success" size="small" fullWidth startIcon={<ShieldCheck size={14} />}>{t("Manage 2FA")}</Button>
          </Paper>

        </Grid>
      </Grid>
    </Container>;
}
